import { useEffect, useState } from "react";
import Modal from "@/components/base/Modal";
import { SelectField, TextAreaField, TextField, ToggleSwitch } from "@/components/base/FormControls";
import { CUSTOMER_TYPE_OPTIONS } from "@/lib/constants";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import type { Customer } from "@/types";

interface CustomerFormModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  customer: Customer | null;
}

interface CustomerForm {
  first_name: string;
  last_name: string;
  document: string;
  phone: string;
  whatsapp: string;
  email: string;
  address: string;
  customer_type: string;
  notes: string;
  active: boolean;
}

const EMPTY: CustomerForm = {
  first_name: "",
  last_name: "",
  document: "",
  phone: "",
  whatsapp: "",
  email: "",
  address: "",
  customer_type: "regular",
  notes: "",
  active: true,
};

export default function CustomerFormModal({
  open,
  onClose,
  onSaved,
  customer,
}: CustomerFormModalProps) {
  const toast = useToast();
  const { profile } = useAuth();
  const [form, setForm] = useState<CustomerForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (customer) {
      setForm({
        first_name: customer.first_name ?? "",
        last_name: customer.last_name ?? "",
        document: customer.document ?? "",
        phone: customer.phone ?? "",
        whatsapp: customer.whatsapp ?? "",
        email: customer.email ?? "",
        address: customer.address ?? "",
        customer_type: customer.customer_type ?? "regular",
        notes: customer.notes ?? "",
        active: customer.active ?? true,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, customer]);

  const update = <K extends keyof CustomerForm>(key: K, value: CustomerForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!form.first_name.trim() && !form.last_name.trim()) {
      setError("Escribe al menos el nombre o el apellido del cliente.");
      return;
    }

    const payload = {
      first_name: form.first_name.trim() || "Cliente",
      last_name: form.last_name.trim() || "",
      document: form.document.trim() || null,
      phone: form.phone.trim() || null,
      whatsapp: form.whatsapp.trim() || null,
      email: form.email.trim() || null,
      address: form.address.trim() || null,
      customer_type: form.customer_type || "regular",
      notes: form.notes.trim() || null,
      active: form.active,
      updated_at: new Date().toISOString(),
    };

    setSubmitting(true);
    try {
      if (customer) {
        const { error: updateError } = await supabase
          .from("customers")
          .update(payload)
          .eq("id", customer.id);
        if (updateError) {
          setError("No se pudo actualizar el cliente.");
          return;
        }
        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "editar",
          entity: "customers",
          entityId: customer.id,
          branchId: profile?.branch_id ?? null,
          oldValue: { tipo: customer.customer_type },
          newValue: { nombre: `${payload.first_name} ${payload.last_name}`.trim(), tipo: payload.customer_type },
        });
        toast.success("Cliente actualizado.");
      } else {
        const { data, error: insertError } = await supabase
          .from("customers")
          .insert(payload)
          .select("id")
          .single();
        if (insertError || !data) {
          setError("No se pudo crear el cliente.");
          return;
        }
        await logAudit({
          userId: profile?.id ?? null,
          userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
          action: "crear",
          entity: "customers",
          entityId: data.id,
          branchId: profile?.branch_id ?? null,
          newValue: { nombre: `${payload.first_name} ${payload.last_name}`.trim(), tipo: payload.customer_type },
        });
        toast.success("Cliente creado.");
      }
      onSaved();
      onClose();
    } catch {
      setError("Ocurrió un error inesperado. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={customer ? "Editar cliente" : "Nuevo cliente"}
      subtitle="El tipo de cliente define qué precio paga: regular o mayorista."
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form="customer-form"
            disabled={submitting}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
          >
            {submitting ? <i className="ri-loader-4-line animate-spin text-base"></i> : null}
            {customer ? "Guardar cambios" : "Crear cliente"}
          </button>
        </>
      }
    >
      <form id="customer-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Nombre"
            name="first_name"
            value={form.first_name}
            onChange={(value) => update("first_name", value)}
            placeholder="Ej: Juan"
            required
          />
          <TextField
            label="Apellido"
            name="last_name"
            value={form.last_name}
            onChange={(value) => update("last_name", value)}
            placeholder="Ej: Pérez"
          />
          <TextField
            label="Documento / Cédula"
            name="document"
            value={form.document}
            onChange={(value) => update("document", value)}
            placeholder="Ej: 001-1234567-8"
          />
          <TextField
            label="Teléfono"
            name="phone"
            value={form.phone}
            onChange={(value) => update("phone", value)}
            inputMode="tel"
            placeholder="Ej: 809-555-0101"
          />
          <TextField
            label="WhatsApp"
            name="whatsapp"
            value={form.whatsapp}
            onChange={(value) => update("whatsapp", value)}
            inputMode="tel"
            placeholder="Ej: 809-555-0101"
          />
          <TextField
            label="Correo electrónico"
            name="email"
            value={form.email}
            onChange={(value) => update("email", value)}
            type="email"
            placeholder="cliente@correo.com"
          />
        </div>

        <TextField
          label="Dirección"
          name="address"
          value={form.address}
          onChange={(value) => update("address", value)}
          placeholder="Dirección del cliente"
        />

        <SelectField
          label="Tipo de cliente"
          name="customer_type"
          value={form.customer_type}
          onChange={(value) => update("customer_type", value)}
          options={CUSTOMER_TYPE_OPTIONS}
          hint="Los mayoristas reciben automáticamente el precio mayorista en el POS."
        />

        <TextAreaField
          label="Notas"
          name="notes"
          value={form.notes}
          onChange={(value) => update("notes", value)}
          placeholder="Información adicional del cliente."
          rows={2}
        />

        <ToggleSwitch
          label="Cliente activo"
          description="Los clientes inactivos no se pueden seleccionar en el POS."
          checked={form.active}
          onChange={(checked) => update("active", checked)}
        />

        {error ? (
          <div className="animate-shake flex items-start gap-2.5 rounded-md border border-primary-300 bg-primary-50 px-3.5 py-3 text-sm text-primary-800">
            <span className="mt-0.5 flex h-4 w-4 items-center justify-center">
              <i className="ri-error-warning-line text-base"></i>
            </span>
            <span className="flex-1 leading-snug">{error}</span>
          </div>
        ) : null}
      </form>
    </Modal>
  );
}